/**
 * Modern Social Actions - Tweet and Follow Operations with React Query
 * Clean, focused, and type-safe implementations
 */
'use client';

import { ProfileActionResponseSchema } from '@sweetistics/contracts';
import { type QueryClient, type QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { createElement, startTransition, useCallback, useEffect, useMemo, useOptimistic, useState } from 'react';
import { toast } from 'sonner';
import type { z } from 'zod';
import {
  getUserPreviewCacheEntry,
  resolveUserPreviewCacheKeys,
  type UserPreviewCachePatch,
  upsertUserPreviewCacheEntry,
} from '@/lib/cache/user-preview-cache';
import { toastError } from '@/lib/errors/toast-error';
import { trpcInteractions, trpcProfiles } from '@/lib/trpc/client';
import { createTrpcMutationAdapter } from '@/lib/trpc/mutation-adapter';
import { wrapTrpcMutation } from '@/lib/trpc/mutation-utils';
import { copyToClipboard } from '@/lib/utils/clipboard';
import type { TweetMetrics } from '@/types/tweet';
import { queryKeys } from './query-keys';

interface TweetActionState {
  isBookmarked: boolean;
  isLiked: boolean;
  isRetweeted: boolean;
  metrics: TweetMetrics;
}

type TweetActionsResult = TweetActionState & {
  isLoading: boolean;
  toggleLike: () => void;
  retweet: () => void;
  toggleBookmark: () => void;
  reply: () => void;
  share: () => Promise<void>;
  copyLink: () => Promise<void>;
};

type TweetAction = 'like' | 'unlike' | 'retweet' | 'bookmark' | 'unbookmark';

type OptimisticTweetAction = { type: 'apply'; action: TweetAction } | { type: 'replace'; state: TweetActionState };

const FRIENDS_QUERY_KEY = ['friendimizer'] as const;
const USERS_QUERY_KEY = ['users'] as const;
const RELATIONSHIPS_QUERY_KEY = ['relationships'] as const;

const ACTION_SUCCESS_MESSAGES = {
  like: 'Tweet liked!',
  unlike: 'Like removed',
  retweet: 'Retweeted!',
  bookmark: 'Tweet bookmarked!',
  unbookmark: 'Bookmark removed',
} as const satisfies Record<TweetAction, string>;

const invalidateQuerySafely = (client: QueryClient, queryKey: QueryKey) => {
  client.invalidateQueries({ queryKey }).catch(() => {
    /* best-effort invalidation; network errors already surface in UI */
  });
};

function applyTweetAction(state: TweetActionState, action: TweetAction): TweetActionState {
  const baseMetrics = state.metrics;

  if (action === 'like') {
    if (state.isLiked) {
      return state;
    }
    return {
      ...state,
      isLiked: true,
      metrics: {
        ...baseMetrics,
        likeCount: (baseMetrics.likeCount ?? 0) + 1,
      },
    };
  }

  if (action === 'unlike') {
    if (!state.isLiked) {
      return state;
    }
    return {
      ...state,
      isLiked: false,
      metrics: {
        ...baseMetrics,
        likeCount: Math.max(0, (baseMetrics.likeCount ?? 0) - 1),
      },
    };
  }

  if (action === 'retweet') {
    if (state.isRetweeted) {
      return state;
    }
    return {
      ...state,
      isRetweeted: true,
      metrics: {
        ...baseMetrics,
        retweetCount: (baseMetrics.retweetCount ?? 0) + 1,
      },
    };
  }

  if (action === 'bookmark') {
    if (state.isBookmarked) {
      return state;
    }
    return {
      ...state,
      isBookmarked: true,
      metrics: {
        ...baseMetrics,
        bookmarkCount: (baseMetrics.bookmarkCount ?? 0) + 1,
      },
    };
  }

  if (action === 'unbookmark') {
    if (!state.isBookmarked) {
      return state;
    }
    return {
      ...state,
      isBookmarked: false,
      metrics: {
        ...baseMetrics,
        bookmarkCount: Math.max(0, (baseMetrics.bookmarkCount ?? 0) - 1),
      },
    };
  }

  return state;
}

type ActionResponse = z.infer<typeof ProfileActionResponseSchema>;

const normalizeActionResponse = (raw: unknown): ActionResponse => ProfileActionResponseSchema.parse(raw);

// Tweet Actions
export function useTweetActions(
  tweetId: string,
  initialMetrics?: TweetMetrics,
  authorUsername?: string
): TweetActionsResult {
  const queryClient = useQueryClient();
  const key = useMemo(() => ['tweet-actions', tweetId] as const, [tweetId]);

  const defaultMetrics = useMemo<TweetMetrics>(() => {
    if (!initialMetrics) {
      return {
        likeCount: 0,
        retweetCount: 0,
        replyCount: 0,
        quoteCount: 0,
        impressionCount: 0,
        bookmarkCount: 0,
        viewCount: 0,
      };
    }

    return {
      likeCount: initialMetrics.likeCount,
      retweetCount: initialMetrics.retweetCount,
      replyCount: initialMetrics.replyCount,
      quoteCount: initialMetrics.quoteCount,
      impressionCount: initialMetrics.impressionCount ?? 0,
      bookmarkCount: initialMetrics.bookmarkCount ?? 0,
      viewCount: initialMetrics.viewCount ?? 0,
    };
  }, [initialMetrics]);

  const defaultState = useMemo<TweetActionState>(
    () => ({
      metrics: defaultMetrics,
      isLiked: false,
      isRetweeted: false,
      isBookmarked: false,
    }),
    [defaultMetrics]
  );

  const { data: state } = useQuery({
    queryKey: key,
    queryFn: () => defaultState,
    initialData: defaultState,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const baseState = state ?? defaultState;

  const [optimisticState, applyOptimisticState] = useOptimistic<TweetActionState, OptimisticTweetAction>(
    baseState,
    (previousState, action) => {
      switch (action.type) {
        case 'apply': {
          return applyTweetAction(previousState, action.action);
        }
        case 'replace': {
          return action.state;
        }
        default: {
          return previousState;
        }
      }
    }
  );

  const likeMutation = wrapTrpcMutation(
    trpcInteractions.like.useMutation({ trpc: { abortOnUnmount: true } }),
    normalizeActionResponse,
    (error) => (error instanceof Error ? error : new Error('Failed to like tweet'))
  );
  const retweetMutation = wrapTrpcMutation(
    trpcInteractions.retweet.useMutation({ trpc: { abortOnUnmount: true } }),
    normalizeActionResponse,
    (error) => (error instanceof Error ? error : new Error('Failed to retweet'))
  );
  const bookmarkMutation = wrapTrpcMutation(
    trpcInteractions.bookmark.useMutation({ trpc: { abortOnUnmount: true } }),
    normalizeActionResponse,
    (error) => (error instanceof Error ? error : new Error('Failed to bookmark tweet'))
  );

  const mutateAsync = useCallback(
    async (action: TweetAction) => {
      const handlers: Record<TweetAction, () => Promise<ActionResponse>> = {
        like: () => likeMutation.mutateAsync({ tweetId }),
        unlike: () => likeMutation.mutateAsync({ tweetId }),
        retweet: () => retweetMutation.mutateAsync({ tweetId }),
        bookmark: () => bookmarkMutation.mutateAsync({ tweetId }),
        unbookmark: () => bookmarkMutation.mutateAsync({ tweetId }),
      };

      const handler = handlers[action];
      if (!handler) {
        throw new Error(`Unknown action: ${String(action)}`);
      }

      const response = normalizeActionResponse(await handler());

      if (!response.success) {
        throw new Error(response.message || `Failed to ${String(action)} tweet`);
      }

      return response;
    },
    [bookmarkMutation, likeMutation, retweetMutation, tweetId]
  );

  const onMutate = useCallback(
    async (action: TweetAction) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TweetActionState>(key) ?? defaultState;
      const next = applyTweetAction(previous, action);
      queryClient.setQueryData(key, next);
      startTransition(() => {
        applyOptimisticState({ type: 'apply', action });
      });
      return { previous };
    },
    [applyOptimisticState, defaultState, key, queryClient]
  );

  const onError = useCallback(
    (error: unknown, action: TweetAction, context: { previous?: TweetActionState } | undefined) => {
      const previousState = context?.previous ?? defaultState;
      queryClient.setQueryData(key, previousState);
      startTransition(() => {
        applyOptimisticState({ type: 'replace', state: previousState });
      });

      const toastMessage = `Failed to ${action} tweet`;
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      toastError({ error: normalizedError, title: toastMessage });
    },
    [applyOptimisticState, defaultState, key, queryClient]
  );

  const onSuccess = useCallback(
    (action: TweetAction, context: { previous?: TweetActionState } | undefined) => {
      toast.success(ACTION_SUCCESS_MESSAGES[action]);

      const base = context?.previous ?? defaultState;
      const resolved = queryClient.getQueryData<TweetActionState>(key) ?? applyTweetAction(base, action);
      startTransition(() => {
        applyOptimisticState({ type: 'replace', state: resolved });
      });
    },
    [applyOptimisticState, defaultState, key, queryClient]
  );

  // Action handlers
  const current = optimisticState;
  // Use mutate (not mutateAsync) to avoid unhandled promise rejections in UI/tests.
  const [isMutating, setIsMutating] = useState(false);

  const runAction = useCallback(
    async (action: TweetAction) => {
      setIsMutating(true);
      const context = await onMutate(action);
      try {
        await mutateAsync(action);
        onSuccess(action, context);
      } catch (error) {
        onError(error, action, context);
        throw error;
      } finally {
        await queryClient.invalidateQueries({ queryKey: key });
        await queryClient.invalidateQueries({ queryKey: ['tweet-metrics', tweetId] });
        await queryClient.invalidateQueries({ queryKey: queryKeys.tweet(tweetId) });
        setIsMutating(false);
      }
    },
    [key, mutateAsync, onError, onMutate, onSuccess, queryClient, tweetId]
  );

  const runActionSafely = useCallback(
    (action: TweetAction) => {
      runAction(action).catch(() => {
        /* errors surfaced via runAction */
      });
    },
    [runAction]
  );

  const toggleLike = useCallback(() => {
    runActionSafely(current.isLiked ? 'unlike' : 'like');
  }, [current.isLiked, runActionSafely]);
  const retweet = useCallback(() => {
    if (current.isRetweeted) {
      return;
    }
    runActionSafely('retweet');
  }, [current.isRetweeted, runActionSafely]);
  const toggleBookmark = useCallback(() => {
    runActionSafely(current.isBookmarked ? 'unbookmark' : 'bookmark');
  }, [current.isBookmarked, runActionSafely]);

  const tweetUrl = authorUsername
    ? `https://twitter.com/${authorUsername}/status/${tweetId}`
    : `https://twitter.com/i/web/status/${tweetId}`;

  const reply = useCallback(() => {
    window.open(`https://twitter.com/intent/tweet?in_reply_to=${tweetId}`, '_blank', 'width=550,height=420');
  }, [tweetId]);

  const copyLink = useCallback(async () => {
    await copyToClipboard(tweetUrl, {
      onSuccess: () => {
        toast.success('Link copied to clipboard!');
      },
      onError: (error) => {
        toast.error('Failed to copy link', {
          description: error instanceof Error ? error.message : undefined,
        });
      },
    });
  }, [tweetUrl]);

  const share = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Share Tweet', url: tweetUrl });
        return;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
      }
    }

    await copyLink();
  }, [copyLink, tweetUrl]);

  // Provide fallback state if query hasn't loaded yet
  return {
    // State
    ...current,
    isLoading: isMutating,

    // Actions
    toggleLike,
    retweet,
    toggleBookmark,
    reply,
    share,
    copyLink,
  };
}

// Follow Actions
type FollowStateSnapshot = {
  isFollowing: boolean;
  hasExplicitValue: boolean;
};

type FollowActionsResult = {
  isFollowing: boolean;
  isLoading: boolean;
  toggleFollow: () => void;
};

export function useFollowActions(
  profileId: string | null,
  username: string,
  initialIsFollowing?: boolean | null
): FollowActionsResult {
  const queryClient = useQueryClient();
  const key = useMemo(() => queryKeys.followStatus(profileId ?? 'unknown'), [profileId]);
  const defaultFollowState = useMemo<FollowStateSnapshot>(
    () => ({ isFollowing: initialIsFollowing ?? false, hasExplicitValue: initialIsFollowing !== undefined }),
    [initialIsFollowing]
  );
  const { data: state } = useQuery<FollowStateSnapshot>({
    queryKey: key,
    queryFn: () => defaultFollowState,
    initialData: defaultFollowState,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: profileId != null && !!username && username !== 'placeholder',
  });

  let baseFollowState = state ?? defaultFollowState;
  if (state?.hasExplicitValue) {
    baseFollowState = state;
  } else if (defaultFollowState.hasExplicitValue) {
    baseFollowState = defaultFollowState;
  }

  type OptimisticFollowAction = { type: 'set'; isFollowing: boolean } | { type: 'replace'; state: FollowStateSnapshot };

  const [optimisticFollowState, setOptimisticFollowState] = useOptimistic(
    baseFollowState,
    (current, action: OptimisticFollowAction) => {
      switch (action.type) {
        case 'set': {
          if (current.isFollowing === action.isFollowing && current.hasExplicitValue) {
            return current;
          }
          return { isFollowing: action.isFollowing, hasExplicitValue: true };
        }
        case 'replace': {
          return action.state;
        }
        default: {
          return current;
        }
      }
    }
  );

  useEffect(() => {
    if (profileId == null || !username || username === 'placeholder') {
      return;
    }

    queryClient.setQueryData(key, (existing: FollowStateSnapshot | undefined) => {
      if (!existing) {
        return defaultFollowState;
      }
      if (defaultFollowState.hasExplicitValue) {
        if (existing.hasExplicitValue && existing.isFollowing === defaultFollowState.isFollowing) {
          return existing;
        }
        return defaultFollowState;
      }
      return existing;
    });
    if (defaultFollowState.hasExplicitValue) {
      startTransition(() => {
        setOptimisticFollowState({ type: 'replace', state: defaultFollowState });
      });
    }
  }, [defaultFollowState, key, queryClient, username, profileId, setOptimisticFollowState]);

  const updateHoverCacheFollowState = useCallback(
    (nextIsFollowing: boolean) => {
      if (profileId == null && !username) {
        return;
      }
      const keyParameters: Parameters<typeof resolveUserPreviewCacheKeys>[0] = {};
      if (profileId !== null) {
        keyParameters.profileId = profileId;
      }
      if (username) {
        keyParameters.username = username;
      }
      const keys = resolveUserPreviewCacheKeys(keyParameters);
      if (keys.length === 0) {
        return;
      }
      for (const cacheKey of keys) {
        const entry = getUserPreviewCacheEntry(cacheKey);
        if (!entry) continue;
        const nextPreview = entry.preview ? { ...entry.preview, isFollowing: nextIsFollowing } : entry.preview;
        const nextProfile = entry.profile ? { ...entry.profile, isFollowing: nextIsFollowing } : entry.profile;
        const patch: UserPreviewCachePatch = {
          detail: entry.detail,
          error: entry.error,
        };
        if (nextPreview !== undefined) {
          patch.preview = nextPreview;
        }
        if (nextProfile !== undefined) {
          patch.profile = nextProfile;
        }
        upsertUserPreviewCacheEntry(cacheKey, patch);
      }
    },
    [profileId, username]
  );

  const followMutation = wrapTrpcMutation(
    createTrpcMutationAdapter(trpcProfiles.followProfile, { trpc: { abortOnUnmount: true } }),
    normalizeActionResponse,
    (error) => (error instanceof Error ? error : new Error('Follow request failed'))
  );
  const [isFollowMutating, setIsFollowMutating] = useState(false);

  const onFollowMutate = async (action: 'follow' | 'unfollow') => {
    if (profileId) {
      await queryClient.cancelQueries({ queryKey: key });
    }

    const previous = queryClient.getQueryData<FollowStateSnapshot>(key) ?? defaultFollowState;
    const next: FollowStateSnapshot = { isFollowing: action === 'follow', hasExplicitValue: true };
    queryClient.setQueryData(key, next);
    startTransition(() => {
      setOptimisticFollowState({ type: 'set', isFollowing: next.isFollowing });
    });
    updateHoverCacheFollowState(next.isFollowing);
    return { previous };
  };

  const onFollowError = (
    error: unknown,
    action: 'follow' | 'unfollow',
    context: { previous?: FollowStateSnapshot } | undefined
  ) => {
    const previousState = context?.previous ?? defaultFollowState;
    queryClient.setQueryData(key, previousState);
    startTransition(() => {
      setOptimisticFollowState({ type: 'replace', state: previousState });
    });
    updateHoverCacheFollowState(previousState.isFollowing);

    const toastMessage = `Failed to ${String(action)}`;
    const normalizedError = error instanceof Error ? error : new Error(String(error));
    toastError({ error: normalizedError, title: toastMessage });
  };

  const onFollowSuccess = (response: ActionResponse, action: 'follow' | 'unfollow') => {
    const message = response.message || (action === 'follow' ? `Followed @${username}` : `Unfollowed @${username}`);
    const undoAction: 'follow' | 'unfollow' = action === 'follow' ? 'unfollow' : 'follow';

    let activeToastId: string | number | undefined;
    const undoState = { inFlight: false };
    const handleUndoClick = () => {
      if (activeToastId !== undefined) {
        toast.dismiss(activeToastId);
        activeToastId = undefined;
      }
      if (undoState.inFlight) {
        return;
      }
      undoState.inFlight = true;
      runFollowMutation(undoAction)
        .catch(() => {
          // Errors already surface through runFollowMutation toast handling.
        })
        .finally(() => {
          undoState.inFlight = false;
        });
    };

    activeToastId = toast.success(
      createElement(
        'div',
        { className: 'flex w-full items-center gap-3 text-left' },
        createElement('span', { className: 'flex-1 font-medium leading-5 text-foreground' }, message),
        createElement(
          'button',
          {
            type: 'button',
            className:
              'shrink-0 text-sm font-semibold text-primary underline underline-offset-4 transition-colors hover:text-primary/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
            onClick: handleUndoClick,
          },
          'Undo'
        )
      ),
      {
        duration: 7000,
      }
    );

    // Invalidate related data (best effort)
    invalidateQuerySafely(queryClient, FRIENDS_QUERY_KEY);
    invalidateQuerySafely(queryClient, USERS_QUERY_KEY);
    if (profileId) {
      invalidateQuerySafely(queryClient, ['tweets', profileId]);
      invalidateQuerySafely(queryClient, ['profiles', profileId]);
    }

    const resolved =
      queryClient.getQueryData<FollowStateSnapshot>(key) ??
      ({
        isFollowing: action === 'follow',
        hasExplicitValue: true,
      } satisfies FollowStateSnapshot);
    startTransition(() => {
      setOptimisticFollowState({ type: 'replace', state: resolved });
    });
  };

  const runFollowMutation = async (action: 'follow' | 'unfollow') => {
    if (profileId == null) {
      throw new Error('Profile ID missing');
    }
    setIsFollowMutating(true);
    const context = await onFollowMutate(action);
    try {
      const response = normalizeActionResponse(await followMutation.mutateAsync({ profileId, action }));
      if (!response.success) {
        throw new Error(response.message || `Failed to ${String(action)} user`);
      }
      onFollowSuccess(response, action);
    } catch (error) {
      onFollowError(error, action, context);
      throw error;
    } finally {
      setIsFollowMutating(false);
    }
  };

  const isFollowing = optimisticFollowState.isFollowing;
  const toggleFollow = () => {
    if (profileId == null) {
      toast.error('Profile unavailable');
      return;
    }
    runFollowMutation(isFollowing ? 'unfollow' : 'follow').catch(() => {
      /* errors already surface via runFollowMutation */
    });
  };

  return {
    // State
    isFollowing,
    isLoading: isFollowMutating || followMutation.isPending,

    // Actions
    toggleFollow,
  };
}

// Mute Actions
type MuteActionsResult = {
  isMuted: boolean;
  isLoading: boolean;
  toggleMute: () => void;
};

export function useMuteActions(profileId: string | null, username: string, initialIsMuted = false): MuteActionsResult {
  const queryClient = useQueryClient();
  const key = queryKeys.muteStatus(profileId ?? 'unknown');
  const { data: state } = useQuery({
    queryKey: key,
    queryFn: () => ({ isMuted: initialIsMuted }),
    initialData: { isMuted: initialIsMuted },
    staleTime: Number.POSITIVE_INFINITY,
    enabled: profileId !== undefined && !!username && username !== 'placeholder',
  });

  // Mute mutation
  const muteMutation = wrapTrpcMutation(
    createTrpcMutationAdapter(trpcProfiles.muteProfile, { trpc: { abortOnUnmount: true } }),
    normalizeActionResponse,
    (error) => (error instanceof Error ? error : new Error('Mute request failed'))
  );

  const runMuteMutation = async (action: 'mute' | 'unmute') => {
    if (profileId == null) {
      throw new Error('Profile ID missing');
    }

    if (profileId) {
      await queryClient.cancelQueries({ queryKey: key });
    }

    const previous = queryClient.getQueryData<{ isMuted: boolean }>(key) ?? { isMuted: initialIsMuted };
    queryClient.setQueryData(key, { isMuted: action === 'mute' });

    try {
      const response = normalizeActionResponse(await muteMutation.mutateAsync({ profileId, action }));
      if (!response.success) {
        throw new Error(response.message || `Failed to ${String(action)} user`);
      }

      const message = response.message || (action === 'mute' ? `Muted @${username}` : `Unmuted @${username}`);
      toast.success(message);

      invalidateQuerySafely(queryClient, RELATIONSHIPS_QUERY_KEY);
      invalidateQuerySafely(queryClient, USERS_QUERY_KEY);
      if (profileId) {
        invalidateQuerySafely(queryClient, ['tweets', profileId]);
        invalidateQuerySafely(queryClient, ['profiles', profileId]);
      }

      return;
    } catch (error) {
      queryClient.setQueryData(key, previous);
      const title = `Failed to ${String(action)}`;
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      toastError({ error: normalizedError, title });
      throw error;
    }
  };

  const isMuted = state?.isMuted ?? (username === 'placeholder' ? false : initialIsMuted);
  const toggleMute = () => {
    if (profileId == null) {
      toast.error('Profile unavailable');
      return;
    }
    runMuteMutation(isMuted ? 'unmute' : 'mute').catch(() => {
      /* errors already surface via runMuteMutation */
    });
  };

  return {
    // State
    isMuted,
    isLoading: muteMutation.isPending,

    // Actions
    toggleMute,
  };
}
